export type RitualPendingIntent<T> =
  | { kind: 'switch'; value: T }
  | { kind: 'close' }
  | { kind: 'navigate' };

export interface RitualIntentRequest<T> {
  pending: RitualPendingIntent<T> | null;
  continueWith: RitualPendingIntent<T> | null;
}

export function requestRitualIntent<T>(dirty: boolean, intent: RitualPendingIntent<T>): RitualIntentRequest<T> {
  return dirty
    ? { pending: intent, continueWith: null }
    : { pending: null, continueWith: intent };
}

export interface RitualDecision<T> {
  continueWith: RitualPendingIntent<T> | null;
  cancelBlockedNavigation: boolean;
  clearDirty: boolean;
}

export function resolveRitualDecision<T>(
  pending: RitualPendingIntent<T>,
  decision: 'discard' | 'cancel' | 'save-failed',
): RitualDecision<T> {
  if (decision === 'discard') return { continueWith: pending, cancelBlockedNavigation: false, clearDirty: true };
  return { continueWith: null, cancelBlockedNavigation: pending.kind === 'navigate', clearDirty: false };
}

export interface RitualPersistenceResult {
  saved: boolean;
  error: string | null;
}

export function attemptRitualPersistence<T>(value: T, persist: (value: T) => boolean): RitualPersistenceResult {
  try {
    return persist(value)
      ? { saved: true, error: null }
      : { saved: false, error: 'Could not save Ritual. Your changes are still unsaved.' };
  } catch {
    return { saved: false, error: 'Could not save Ritual. Your changes are still unsaved.' };
  }
}

export function continueRitualIntent<T>(
  intent: RitualPendingIntent<T>,
  handlers: { load(value: T): void; close(): void; navigate(): void },
): void {
  if (intent.kind === 'switch') handlers.load(intent.value);
  else if (intent.kind === 'close') handlers.close();
  else handlers.navigate();
}

interface BeforeUnloadTarget {
  addEventListener(type: 'beforeunload', listener: (event: BeforeUnloadEvent) => void): void;
  removeEventListener(type: 'beforeunload', listener: (event: BeforeUnloadEvent) => void): void;
}

export function protectRitualBeforeUnload(
  target: BeforeUnloadTarget,
  dirty: boolean,
): () => void {
  if (!dirty) return () => undefined;
  const listener = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
    event.returnValue = '';
  };
  target.addEventListener('beforeunload', listener);
  return () => target.removeEventListener('beforeunload', listener);
}
